// src/components/GardenEditorUI.tsx

import React, { useState, useEffect } from 'react';
import {
    Box, Button, Card, CardActions, TextField, Typography, Dialog,
    DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem,
    Alert, List, ListItemButton, ListItemIcon, ListItemText, Tooltip, Stack
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import DoneIcon from '@mui/icons-material/Done';
import VerifiedIcon from '@mui/icons-material/Verified';
import { ExistingGarden, ActiveModal } from '../../hooks/useGardenEditor';
import LoginModal from '../LoginModal';
import { themeColors } from '../../styles/theme';

// --- PROPS ---
interface GardenEditorUIProps {
    state: {
        isSubmitting: boolean;
        error: string | null;
        pubHasNoGarden: boolean; // Added
        isDrawing: boolean;
        showSidePanel: boolean;
        activeModal: ActiveModal;
        profile: any;
        isAnonymous: boolean;
        selectedEditGarden: ExistingGarden | null;
        gardensForSelection: ExistingGarden[];
        canUndo: boolean;
        featureBeingEdited: any;
    };
    actions: {
        onExitAndRefresh: () => void;
        cancelAction: () => void;
        startDrawing: () => void;
        setShowSidePanel: (show: boolean) => void;
        setSelectedEditGarden: (garden: ExistingGarden | null) => void;
        modal: {
            closeAll: () => void;
            open: (modal: ActiveModal) => void;
            onNameSubmit: (name: string) => void;
            onConfirmSubmit: () => void;
            onAnonymousSubmit: (name: string) => Promise<void>;
            onStartEditOutline: () => void;
            onToggleUncertainty: () => Promise<void>;
            onDelete: () => Promise<void>;
            onMarkNoGarden: () => Promise<void>; // Added
            onFeedbackSubmit: (type: 'outline' | 'elevation' | 'other', message?: string) => Promise<any>;
            onApprove: () => Promise<void>; // Added for staff approval
            onUndo: () => void;
            onFinishDrawing: () => void;
            onCancelDrawing: () => void;
            onFinishEditing: () => void;
        };
    };
}

const getOrdinalSuffix = (n: number) => {
    if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;
    switch (n % 10) {
        case 1: return `${n}st`;
        case 2: return `${n}nd`;
        case 3: return `${n}rd`;
        default: return `${n}th`;
    }
};

const DialogActionButtonStyle = {
    textTransform: 'none',
    fontWeight: 500,
    borderRadius: '48px',
    padding: '6px 10px',
    fontSize: '0.9rem',
    background: 'transparent',
    color: '#000',
    border: `1px solid black`,
    '&:hover': {
        background: 'rgba(0, 0, 0, 0.05)',
        borderColor: 'black',
    },
    minWidth: '80px',
    mb: 1,
    mr: 0.5
}

const IconButtonStyle = {
    textTransform: 'none',
    fontWeight: 500,
    borderRadius: '48px',
    padding: '6px 10px',
    fontSize: '0.9rem',
    backgroundColor: themeColors.lager,
    color: '#000',
    border: `1px solid black`,
    '&:hover': {
        background: 'rgba(0, 0, 0, 0.05)',
        borderColor: 'black',
    },
    minWidth: '80px',
    flexShrink: 0,
    flexGrow: 1,
    textAlign: 'center',
    mr: 0.5,
    mb: 1,
    fontFamily: "ApfelGrotezkRegular",
}


// --- DRAWING CONTROLS ---
const DrawingControls: React.FC<{
    onUndo: () => void,
    onFinish: () => void,
    onCancel: () => void,
    canUndo: boolean,
    isEditing?: boolean
}> = ({ onUndo, onFinish, onCancel, canUndo, isEditing }) => {
    return (
        <Card sx={{
            position: 'absolute',
            bottom: { xs: '80px', sm: '20px' },
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1,
            p: 1,
        }}>
            <CardActions>
                <Button
                    sx={{...IconButtonStyle, backgroundColor: 'transparent'}}
                    onClick={onCancel}
                    variant="outlined"
                    color="error"
                    startIcon={<CancelIcon />}
                >
                    Cancel
                </Button>
                <Tooltip title={isEditing ? "Save changes" : "Finish the shape (requires at least 3 points)"}>
                    <Button
                        sx={IconButtonStyle}
                        onClick={onFinish}
                        variant="contained"
                        color="primary"
                        endIcon={<DoneIcon />}
                    >
                        {isEditing ? 'Save' : 'Submit'}
                    </Button>
                </Tooltip>
            </CardActions>
        </Card>
    )
}

// --- COMPONENT ---
export const GardenEditorUI: React.FC<GardenEditorUIProps> = ({ state, actions }) => {
    const { activeModal, selectedEditGarden } = state;

    // Internal form state for modals
    const [gardenName, setGardenName] = useState('');
    const [customGardenName, setCustomGardenName] = useState('');
    const [showCustomNameField, setShowCustomNameField] = useState(false);
    const [submitterName, setSubmitterName] = useState('');
    const [feedbackMessage, setFeedbackMessage] = useState('');
    const [feedbackStatus, setFeedbackStatus] = useState<{ type: 'success' | 'error', message: string } | null>(null);

    useEffect(() => {
        // Fetch stored name for anonymous users
        if (state.isAnonymous) {
            const storedName = localStorage.getItem('suntap_submitter_name');
            if (storedName) setSubmitterName(storedName);
        }
    }, [state.isAnonymous]);

    useEffect(() => {
        // Reset name form when dialog opens
        if (activeModal === 'name') {
            resetNameForm();
        }
    }, [activeModal]);

    const handleNameNext = () => {
        const finalName = showCustomNameField && customGardenName.trim() ? customGardenName.trim() : gardenName;
        if (finalName) actions.modal.onNameSubmit(finalName);
    };

    const handleFeedbackSubmit = async (type: 'outline' | 'elevation' | 'other', message: string = '') => {
        setFeedbackStatus(null);
        try {
            await actions.modal.onFeedbackSubmit(type, message);
            setFeedbackStatus({ type: 'success', message: 'Thank you! Your feedback has been submitted.' });
            setTimeout(() => {
                actions.modal.closeAll();
                actions.cancelAction();
            }, 2500);
        } catch (err: any) {
            setFeedbackStatus({ type: 'error', message: err.message || 'Failed to submit feedback.' });
        }
    };

    const resetNameForm = () => {
        setGardenName('');
        setCustomGardenName('');
        setShowCustomNameField(false);
    };

    return (
        <>
            {/* --- DRAWING CONTROLS --- */}
            {state.isDrawing && (
                <DrawingControls
                    onUndo={actions.modal.onUndo}
                    onFinish={state.featureBeingEdited ? actions.modal.onFinishEditing : actions.modal.onFinishDrawing}
                    onCancel={actions.modal.onCancelDrawing}
                    canUndo={state.canUndo}
                    isEditing={!!state.featureBeingEdited}
                />
            )}

            {/* --- SIDE PANEL --- */}
            {!state.showSidePanel && activeModal === 'none' && (
                <Button
                    variant="contained"
                    onClick={() => actions.setShowSidePanel(true)}
                    sx={{ ...IconButtonStyle, position: 'absolute', top: '20px', right: '20px', zIndex: 1, minWidth: 'auto', px: 2 }}
                    startIcon={<EditIcon />}
                >
                    Garden Editor
                </Button>
            )}

            {/* --- DIALOGS --- */}
            <Dialog
                open={activeModal === 'initial-prompt'}
                onClose={actions.cancelAction}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle
                    sx={{ fontFamily: "ApfelGrotezkRegular", fontWeight: 700 }}
                >
                    {state.pubHasNoGarden ? "This Pub Was Marked as Having No Garden" : "No Garden Mapped Yet"}
                </DialogTitle>
                <DialogContent sx={{ fontFamily: "ApfelGrotezkRegular", fontWeight: 500 }} >
                    <Typography>
                        {state.pubHasNoGarden
                            ? "This pub was previously marked as having no outdoor space. If this is incorrect, you can proceed to map the garden."
                            : "Help map the garden to get the sun forecast."
                        }
                    </Typography>
                    <Stack direction="column" sx={{ mt: 1 }}>
                    <Button
                        variant="contained"
                        endIcon={<AddIcon />}
                        onClick={() => { actions.modal.closeAll(); actions.startDrawing(); }}
                        fullWidth
                        sx={IconButtonStyle}
                    >
                        {state.pubHasNoGarden ? "Actually, It Does Have a Garden" : "Map Garden"}
                    </Button>
                    {!state.pubHasNoGarden && (
                        <Button
                            variant="text"
                            onClick={() => {
                                if (window.confirm('Are you sure this pub has no open garden space? This action cannot be easily undone.')) {
                                    actions.modal.onMarkNoGarden();
                                }
                            }}
                            disabled={state.isSubmitting}
                            fullWidth
                            sx={IconButtonStyle}
                            endIcon={<DoNotDisturbIcon />}
                        >
                            Pub has no open garden
                        </Button>
                    )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        onClick={actions.cancelAction}
                        sx={DialogActionButtonStyle}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

             <Dialog open={activeModal === 'drawing-guide'} onClose={() => actions.modal.open('none')} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily: "ApfelGrotezkRegular" }} >Step 1: Draw Outline</DialogTitle>
                <DialogContent>
                    <Typography>Click on the map to drop points around the edge of the garden (at least 3). When you're done, press the 'Submit' button.</Typography>
                    <Typography sx={{ mt: 2, fontStyle: 'italic', color: 'text.secondary' }}>
                        Please draw the garden as accurately as you can - this helps everyone get the best sun forecasts! ☀️
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => actions.modal.open('none')}
                        variant="contained"
                        sx={{...DialogActionButtonStyle, mr: 2}}
                    >
                        OK, Got It
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={activeModal === 'update'} onClose={actions.cancelAction} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily: "ApfelGrotezkRegular" }} >Update this pub</DialogTitle>
                <DialogContent>
                    <Stack direction="column" sx={{ mt: 1 }}>
                        <Button
                            sx={IconButtonStyle}
                            onClick={() => { actions.modal.closeAll(); actions.startDrawing(); }}
                            endIcon={<AddIcon />}
                        >
                            Add another garden
                        </Button>
                        <Button
                            sx={IconButtonStyle}
                            onClick={() => actions.modal.open('selectGarden')}
                            disabled={!state?.gardensForSelection?.length}
                            endIcon={<EditIcon />}
                        >
                            Edit a mapped garden
                        </Button>

                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        sx={DialogActionButtonStyle}
                        onClick={actions.cancelAction}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={activeModal === 'selectGarden'} onClose={() => actions.modal.open('update')} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily: "ApfelGrotezkRegular" }} >Select garden to edit</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 1 }}>
                        <InputLabel sx={{ color: 'black', fontWeight: 500, fontFamily: "ApfelGrotezkRegular" }} >Garden</InputLabel>
                        <Select
                            value={selectedEditGarden?.uid || ''}
                            onChange={(e) => actions.setSelectedEditGarden(state.gardensForSelection.find(g => g.uid === e.target.value) || null)}
                            label="Garden"
                        >
                            {state.gardensForSelection.map(g => {
                                const canEdit = state.profile?.is_staff || !state.isAnonymous || g.is_owner;
                                return (
                                    <MenuItem key={g.uid} value={g.uid} disabled={!canEdit}>
                                        {g.name}{g.is_uncertain && ' (uncertain)'}
                                        {!canEdit && state.isAnonymous && ' (sign in to edit)'}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => actions.modal.open('update')}
                        sx={DialogActionButtonStyle}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => actions.modal.open('editAction')}
                        variant="contained"
                        disabled={!selectedEditGarden}
                        sx={DialogActionButtonStyle}
                    >
                        Select
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={activeModal === 'editAction'} onClose={actions.cancelAction} maxWidth="sm" fullWidth>
                <DialogTitle  sx={{ fontFamily: "ApfelGrotezkRegular" }}>Edit "{selectedEditGarden?.name}"</DialogTitle>
                <DialogContent>
                    <List>
                        <ListItemButton
                            sx={IconButtonStyle}
                            onClick={actions.modal.onStartEditOutline}>
                            <ListItemIcon><EditIcon /></ListItemIcon>
                            <ListItemText primary="Change outline" />
                        </ListItemButton>
                        <ListItemButton
                            sx={IconButtonStyle}
                            onClick={actions.modal.onToggleUncertainty}>
                            <ListItemIcon>
                                {selectedEditGarden?.is_uncertain ? <CheckCircleOutlineIcon /> : <ReportProblemIcon />}
                            </ListItemIcon>
                            <ListItemText
                                primary={selectedEditGarden?.is_uncertain ? "Mark as certain" : "Mark as uncertain"}
                                secondary="Flag if boundaries are unclear" />
                        </ListItemButton>
                        {state.profile?.is_staff &&
                            <ListItemButton
                                sx={IconButtonStyle}
                                onClick={() => window.confirm('Are you sure you want to approve this garden?') && actions.modal.onApprove()}
                            >
                                <ListItemIcon>
                                    <VerifiedIcon color="success" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Approve garden"
                                    secondary="Mark garden as approved and moderated"
                                    primaryTypographyProps={{ color: 'success' }}
                                />
                            </ListItemButton>}
                        {(state.profile?.is_staff || selectedEditGarden?.is_owner) &&
                            <ListItemButton
                                sx={IconButtonStyle}
                                onClick={() => window.confirm('Are you sure?') && actions.modal.onDelete()}>
                                <ListItemIcon>
                                    <DeleteIcon color="error" />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Delete garden"
                                    primaryTypographyProps={{ color: 'error' }} />
                            </ListItemButton>}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button
                        sx={DialogActionButtonStyle}
                        onClick={actions.cancelAction}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={activeModal === 'verified'} onClose={actions.cancelAction} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily: "ApfelGrotezkRegular" }} >Suntap Verified</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily: "ApfelGrotezkRegular" }}>We've hand-checked this pub's outline, so public edits are locked. Spot an error? Tell us below.</Typography>
                    {feedbackStatus && <Alert severity={feedbackStatus.type} sx={{ mt: 2 }}>{feedbackStatus.message}</Alert>}
                    <Stack direction="column" sx={{ mt: 1 }}>
                        <Button
                            sx={IconButtonStyle}
                            onClick={() => handleFeedbackSubmit('outline')}
                            disabled={state.isSubmitting}
                        >
                            Suggest outline change
                        </Button>
                        <Button
                            sx={IconButtonStyle}
                            onClick={() => { actions.modal.closeAll(); actions.modal.open('differentIssue'); }}
                            disabled={state.isSubmitting}
                        >
                            Different issue...
                        </Button>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={actions.cancelAction}
                        disabled={state.isSubmitting}
                        sx={DialogActionButtonStyle}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={activeModal === 'differentIssue'} onClose={() => actions.modal.open('verified')} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily: 'ApfelGrotezkRegular' }}>Describe the Issue</DialogTitle>
                <DialogContent>
                    {feedbackStatus && <Alert severity={feedbackStatus.type} sx={{ mb: 2 }}>{feedbackStatus.message}</Alert>}
                    <TextField autoFocus fullWidth multiline rows={4} label="Issue description" value={feedbackMessage} onChange={(e) => setFeedbackMessage(e.target.value)} disabled={state.isSubmitting} />
                </DialogContent>
                <DialogActions>
                    <Button
                        sx={DialogActionButtonStyle}
                        onClick={() => actions.modal.open('verified')}
                        disabled={state.isSubmitting}
                    >
                        Back
                    </Button>
                    <Button
                        sx={DialogActionButtonStyle}
                        onClick={() => handleFeedbackSubmit('other', feedbackMessage)}
                        variant="contained"
                        disabled={state.isSubmitting || !feedbackMessage.trim()}
                    >
                        {state.isSubmitting ? 'Submitting...' : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={activeModal === 'name'} onClose={() => actions.modal.onCancelDrawing()} maxWidth="sm" fullWidth>
                <DialogTitle>Step 2: Name this outdoor area</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 2 }}><InputLabel>Select a name</InputLabel><Select value={gardenName} label="Select a name" onChange={(e) => { setGardenName(e.target.value); setShowCustomNameField(e.target.value === 'Custom'); }}><MenuItem value="Front Garden">Front Garden</MenuItem><MenuItem value="Back Garden">Back Garden</MenuItem><MenuItem value="Side Garden">Side Garden</MenuItem><MenuItem value="Rooftop Garden">Rooftop Garden</MenuItem><MenuItem value="Beer Garden">Beer Garden</MenuItem><MenuItem value="Patio">Patio</MenuItem><MenuItem value="Terrace">Terrace</MenuItem><MenuItem value="Custom">Custom Name...</MenuItem></Select></FormControl>
                    {showCustomNameField && <TextField fullWidth label="Enter custom name" value={customGardenName} onChange={(e) => setCustomGardenName(e.target.value)} sx={{ mt: 2 }} />}
                </DialogContent>
                <DialogActions>
                    <Button
                        sx={DialogActionButtonStyle}
                        onClick={() => actions.modal.onCancelDrawing()}
                    >
                        Back
                    </Button>
                    <Button
                        sx={DialogActionButtonStyle}
                        onClick={handleNameNext}
                        variant="contained"
                        disabled={!gardenName || (showCustomNameField && !customGardenName.trim())}
                    >
                        Next
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={activeModal === 'confirm'} onClose={actions.cancelAction} maxWidth="sm" fullWidth>
                <DialogTitle>Submit garden?</DialogTitle>
                <DialogContent><Typography>Happy with the outline?</Typography></DialogContent>
                <DialogActions>
                    <Button
                        sx={DialogActionButtonStyle}
                        onClick={() => actions.modal.open('name')}
                    >
                        Go Back
                    </Button>
                    <Button
                        sx={DialogActionButtonStyle}
                        onClick={actions.modal.onConfirmSubmit}
                        variant="contained"
                        disabled={state.isSubmitting}
                    >
                        {state.isSubmitting ? 'Submitting...' : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={activeModal === 'submitterName'} onClose={() => actions.modal.closeAll()}>
                <DialogTitle>One Last Step!</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>Please provide a name so we can credit you.</Typography>
                    <TextField autoFocus margin="dense" label="Your Name (for attribution)" type="text" fullWidth value={submitterName} onChange={(e) => setSubmitterName(e.target.value)} helperText="This will be publicly visible." />
                    <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>Want to track your contributions? <Button size="small" sx={{ ml: 0.5 }} onClick={() => actions.modal.open('login')}>Create an account</Button></Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        sx={DialogActionButtonStyle}
                        onClick={() => actions.modal.closeAll()}
                        disabled={state.isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button
                        sx={DialogActionButtonStyle}
                        onClick={() => actions.modal.onAnonymousSubmit(submitterName)}
                        variant="contained"
                        disabled={!submitterName.trim() || state.isSubmitting}
                    >
                        {state.isSubmitting ? 'Submitting...' : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={activeModal === 'submitAnother'} onClose={actions.cancelAction} maxWidth="sm" fullWidth>
                <DialogTitle>Garden Submitted!</DialogTitle>
                <DialogContent>
                    <Typography>Would you like to see the sun forecast for your contribution?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        sx={{...DialogActionButtonStyle, ml: 2}}
                        onClick={() => { actions.modal.closeAll(); actions.startDrawing(); }}
                        color="secondary"
                    >
                        No, Map Another Garden
                    </Button>
                    <Button
                        sx={{...DialogActionButtonStyle, mr: 2}}
                        onClick={actions.onExitAndRefresh}
                        variant="contained"
                    >
                        View Sun Forecast
                    </Button>
                </DialogActions>
            </Dialog>
            
            <Dialog 
                open={activeModal === 'authSuccess'} 
                onClose={actions.cancelAction}
                maxWidth="xs" 
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
                    <Box 
                        component="img" 
                        src="/circular_logo_icon.png"
                        alt="Suntap Logo" 
                        sx={{ height: 40, mb: 1.5, display: 'block', mx: 'auto' }} 
                    />
                    Thanks, {state.profile?.display_name || 'friend'}!
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pb: 2 }}>
                    <Typography variant="h6" component="p" sx={{ textAlign: 'center' }}>
                        Your contribution has been submitted.
                    </Typography>
                    {state.profile?.gardens_submitted && (
                        <Typography variant="body1" sx={{ textAlign: 'center' }}>
                            This is your {getOrdinalSuffix(state.profile.gardens_submitted)} contribution.
                        </Typography>
                    )}
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        Would you like to see the sun forecast for your contribution?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 3, pb: 3 }}>
                    <Button
                        sx={DialogActionButtonStyle}
                        onClick={actions.onExitAndRefresh}
                        variant="contained"
                        fullWidth
                    >
                        View Forecast
                    </Button>
                    <Button
                        sx={DialogActionButtonStyle}
                        onClick={() => { actions.modal.closeAll(); actions.startDrawing(); }}
                        color="secondary"
                        fullWidth
                    >
                        No, Map Another Garden
                    </Button>
                </DialogActions>
            </Dialog>
            
            <LoginModal open={activeModal === 'login'} onClose={() => actions.modal.open('submitterName')} />
        </>
    );
};